/**
 * Bill-Lin "Matching readiness" — always computed dynamically from the current
 * workspace name + tag values. Nothing is "stamped" on upload alone: uploading a
 * slip/sheet only affects the score after extracted or manually saved fields
 * satisfy each milestone's rules below.
 *
 * Upload → Extract API → merge into tags → recompute (same tags as พิมพ์ข้อมูลเอง).
 */

export const PROFILE_MAGIC_WEIGHTS = {
  shopName: 10,
  businessType: 10,
  receivingAccount: 25,
  shopDocumentTax: 25,
  outstandingSamples: 15,
  regularCustomers: 10,
  legacySheetUpload: 5,
} as const;

export type ProfileMagicMilestoneId = keyof typeof PROFILE_MAGIC_WEIGHTS;

export type ProfileMagicMilestone = {
  id: ProfileMagicMilestoneId;
  label: string;
  weight: number;
  completed: boolean;
};

export type ProfileMagicReadiness = {
  percent: number;
  earned: number;
  total: number;
  milestones: ProfileMagicMilestone[];
};

import {
  PROFILE_MAGIC_TAG_KEYS,
  canonicalTagMap,
} from "@/lib/profileMagicTagKeys";

export {
  PROFILE_MAGIC_TAG_KEYS,
  readCanonicalTagValue,
} from "@/lib/profileMagicTagKeys";

export type ProfileMagicTagRow = { id?: string; key: string; value: string };

/** Minimum business bank fields for +25% (matches what Bill-Lin matching needs) */

export const PROFILE_MAGIC_RECEIVING_REQUIRED = [
  PROFILE_MAGIC_TAG_KEYS.businessAccountNumber,
] as const;

/** Business doc / tax +25%: tax ID or a real address (not upload flags) */
export const PROFILE_MAGIC_SHOP_DOC_STRONG_KEYS = [
  PROFILE_MAGIC_TAG_KEYS.businessTaxId,
  PROFILE_MAGIC_TAG_KEYS.businessAddress,
] as const;

export type SlipExtractResponse = {
  sender_name?: string | null;
  sender_name_th?: string | null;
  sender_name_en?: string | null;
  sender_bank?: { name?: string | null; short?: string | null } | null;
  recipient_name?: string | null;
  recipient_name_th?: string | null;
  recipient_name_en?: string | null;
};

export type ReceiptBuyerExtractResponse = {
  buyer_name?: string | null;
  buyer_legal_name?: string | null;
  buyer_tax_id?: string | null;
  buyer_branch?: string | null;
  buyer_address?: string | null;
  document_type?: string | null;
};

export type ReceiptSellerExtractResponse = {
  seller_name?: string | null;
  seller_legal_name?: string | null;
  seller_tax_id?: string | null;
  seller_branch?: string | null;
  seller_address?: string | null;
  document_type?: string | null;
};

function tagMap(tags: ProfileMagicTagRow[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const t of tags) {
    const k = t.key.trim();
    const v = t.value.trim();
    if (k && v) m.set(k, v);
  }
  return m;
}

function hasTag(m: Map<string, string>, key: string): boolean {
  return Boolean(m.get(key)?.length);
}

function hasAnyTag(m: Map<string, string>, keys: readonly string[]): boolean {
  return keys.some((k) => hasTag(m, k));
}

function hasJsonArrayTag(m: Map<string, string>, key: string): boolean {
  const raw = m.get(key);
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}

function hasMeaningfulAddress(m: Map<string, string>): boolean {
  const addr = m.get(PROFILE_MAGIC_TAG_KEYS.businessAddress);
  return Boolean(addr && addr.trim().length >= 10);
}

function hasMeaningfulTaxId(m: Map<string, string>): boolean {
  const tax = m.get(PROFILE_MAGIC_TAG_KEYS.businessTaxId);
  if (!tax) return false;
  const digits = tax.replace(/\D/g, "");
  return digits.length >= 10;
}

export function isProfileMagicShopNameDone(
  workspaceName?: string | null,
): boolean {
  return Boolean(workspaceName?.trim());
}

export function isProfileMagicBusinessTypeDone(
  tags: ProfileMagicTagRow[],
): boolean {
  return hasTag(
    canonicalTagMap(tags),
    PROFILE_MAGIC_TAG_KEYS.businessTypeConfig,
  );
}

/** +25% only when business bank tags are usable — not when a slip file was uploaded */
export function isProfileMagicReceivingAccountDone(
  tags: ProfileMagicTagRow[],
): boolean {
  const m = canonicalTagMap(tags);
  if (hasTag(m, PROFILE_MAGIC_TAG_KEYS.businessAccountNumber)) return true;
  const hasName =
    hasTag(m, PROFILE_MAGIC_TAG_KEYS.businessAccountName) ||
    hasJsonArrayTag(m, PROFILE_MAGIC_TAG_KEYS.businessAccountNames);
  return hasTag(m, PROFILE_MAGIC_TAG_KEYS.businessBank) && hasName;
}

function normalizeRecipientName(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

export function recipientNamesEquivalent(a: string, b: string): boolean {
  const left = normalizeRecipientName(a);
  const right = normalizeRecipientName(b);
  if (!left || !right) return false;
  return left === right;
}

/** Parsed recipient names from slip extract (TH + EN when both exist). */
export function recipientNamesFromSlipExtract(data: SlipExtractResponse): {
  th: string;
  en: string;
  names: string[];
  showDual: boolean;
} {
  const th =
    data.recipient_name_th?.trim() || data.recipient_name?.trim() || "";
  const en = data.recipient_name_en?.trim() || "";
  const showDual = Boolean(th && en && !recipientNamesEquivalent(th, en));
  const names = showDual ? [th, en] : [th || en].filter(Boolean);
  return { th, en, names, showDual };
}

export function slipRecipientDisplayName(data: SlipExtractResponse): string {
  const { names } = recipientNamesFromSlipExtract(data);
  return names[0] ?? "";
}

export function isSlipExtractSufficientForReceiving(
  data: SlipExtractResponse,
): boolean {
  return recipientNamesFromSlipExtract(data).names.length > 0;
}

export function applySlipExtractToDraft(data: SlipExtractResponse): {
  accountNameTh: string;
  accountNameEn: string;
  showDualNames: boolean;
  bank: string;
  bankShort: string;
} {
  const { th, en, showDual } = recipientNamesFromSlipExtract(data);
  return {
    accountNameTh: th,
    accountNameEn: en,
    showDualNames: showDual,
    bank: data.sender_bank?.name?.trim() ?? "",
    bankShort: data.sender_bank?.short?.trim() ?? "",
  };
}

/** +25% shop doc — tax ID or substantive address (manual or receipt extract) */
export function isProfileMagicShopDocumentTaxDone(
  tags: ProfileMagicTagRow[],
): boolean {
  const m = canonicalTagMap(tags);
  return hasMeaningfulTaxId(m) || hasMeaningfulAddress(m);
}

export function isReceiptBuyerExtractSufficientForShopDoc(
  data: ReceiptBuyerExtractResponse,
): boolean {
  return Boolean(
    data.buyer_tax_id?.trim() || (data.buyer_address?.trim()?.length ?? 0) >= 10,
  );
}

export function tagsFromReceiptSellerExtract(
  data: ReceiptSellerExtractResponse,
): ProfileMagicTagRow[] {
  const out: ProfileMagicTagRow[] = [];
  const displayName = data.seller_name?.trim() ?? "";
  const legalName = data.seller_legal_name?.trim() ?? "";
  const tax = data.seller_tax_id?.trim();
  const addr = data.seller_address?.trim();
  if (displayName) {
    out.push({
      key: PROFILE_MAGIC_TAG_KEYS.businessDisplayName,
      value: displayName,
    });
  }
  if (legalName) {
    out.push({
      key: PROFILE_MAGIC_TAG_KEYS.businessLegalName,
      value: legalName,
    });
  }
  if (tax) {
    out.push({ key: PROFILE_MAGIC_TAG_KEYS.businessTaxId, value: tax });
  }
  if (addr) {
    out.push({ key: PROFILE_MAGIC_TAG_KEYS.businessAddress, value: addr });
  }
  if (data.seller_branch?.trim()) {
    out.push({
      key: PROFILE_MAGIC_TAG_KEYS.businessBranch,
      value: data.seller_branch.trim(),
    });
  }
  if (data.document_type?.trim()) {
    out.push({
      key: PROFILE_MAGIC_TAG_KEYS.businessDocumentType,
      value: data.document_type.trim(),
    });
  }
  return out;
}

export function applyReceiptExtractToDraft(
  seller: ReceiptSellerExtractResponse | null,
  buyer: ReceiptBuyerExtractResponse | null,
): {
  shopDisplayName: string;
  shopLegalName: string;
  taxId: string;
  address: string;
  branch: string;
  documentType: string;
  partnerName: string;
  partnerAlias: string;
  partnerTaxId: string;
} {
  const buyerLegal = buyer?.buyer_legal_name?.trim() ?? "";
  const buyerDisplay = buyer?.buyer_name?.trim() ?? "";
  return {
    shopDisplayName: seller?.seller_name?.trim() ?? "",
    shopLegalName: seller?.seller_legal_name?.trim() ?? "",
    taxId: seller?.seller_tax_id?.trim() ?? "",
    address: seller?.seller_address?.trim() ?? "",
    branch: seller?.seller_branch?.trim() ?? "",
    documentType:
      seller?.document_type?.trim() ||
      buyer?.document_type?.trim() ||
      "",
    partnerName: buyerLegal || buyerDisplay,
    partnerAlias:
      buyerLegal && buyerDisplay && buyerLegal !== buyerDisplay
        ? buyerDisplay
        : "",
    partnerTaxId: buyer?.buyer_tax_id?.trim() ?? "",
  };
}

export function buildShopDocumentTagPatch(input: {
  shopDisplayName: string;
  shopLegalName: string;
  taxId: string;
  address: string;
  branch: string;
  documentType: string;
}): ProfileMagicTagRow[] {
  return [
    {
      key: PROFILE_MAGIC_TAG_KEYS.businessDisplayName,
      value: input.shopDisplayName.trim(),
    },
    {
      key: PROFILE_MAGIC_TAG_KEYS.businessLegalName,
      value: input.shopLegalName.trim(),
    },
    {
      key: PROFILE_MAGIC_TAG_KEYS.businessTaxId,
      value: input.taxId.trim(),
    },
    {
      key: PROFILE_MAGIC_TAG_KEYS.businessAddress,
      value: input.address.trim(),
    },
    {
      key: PROFILE_MAGIC_TAG_KEYS.businessBranch,
      value: input.branch.trim(),
    },
    {
      key: PROFILE_MAGIC_TAG_KEYS.businessDocumentType,
      value: input.documentType.trim(),
    },
  ];
}

export function isProfileMagicOutstandingSamplesDone(
  tags: ProfileMagicTagRow[],
): boolean {
  return hasJsonArrayTag(
    canonicalTagMap(tags),
    PROFILE_MAGIC_TAG_KEYS.businessOutstandingSamples,
  );
}

export function isProfileMagicRegularCustomersDone(
  tags: ProfileMagicTagRow[],
): boolean {
  return hasJsonArrayTag(
    canonicalTagMap(tags),
    PROFILE_MAGIC_TAG_KEYS.customerAliases,
  );
}

/** +5% when legacy sheet has parsed/imported rows — not upload-only */
export function isProfileMagicLegacySheetDone(tags: ProfileMagicTagRow[]): boolean {
  return hasJsonArrayTag(
    canonicalTagMap(tags),
    PROFILE_MAGIC_TAG_KEYS.businessLegacySheetRows,
  );
}

const MILESTONE_LABELS: Record<ProfileMagicMilestoneId, string> = {
  shopName: "ตั้งชื่อร้านแล้ว",
  businessType: "เลือกประเภทร้านแล้ว",
  receivingAccount: "เพิ่มบัญชีรับเงิน",
  shopDocumentTax: "เพิ่มข้อมูลเอกสารร้าน / เลขภาษี",
  outstandingSamples: "เพิ่มตัวอย่างยอดค้าง",
  regularCustomers: "เพิ่มลูกค้าประจำ / alias",
  legacySheetUpload: "นำเข้าชีทเก่า (มีข้อมูลแล้ว)",
};

export function computeProfileMagicReadiness(input: {
  workspaceName?: string | null;
  tags: ProfileMagicTagRow[];
}): ProfileMagicReadiness {
  const { workspaceName, tags } = input;

  const completedById: Record<ProfileMagicMilestoneId, boolean> = {
    shopName: isProfileMagicShopNameDone(workspaceName),
    businessType: isProfileMagicBusinessTypeDone(tags),
    receivingAccount: isProfileMagicReceivingAccountDone(tags),
    shopDocumentTax: isProfileMagicShopDocumentTaxDone(tags),
    outstandingSamples: isProfileMagicOutstandingSamplesDone(tags),
    regularCustomers: isProfileMagicRegularCustomersDone(tags),
    legacySheetUpload: isProfileMagicLegacySheetDone(tags),
  };

  const milestones = (
    Object.keys(PROFILE_MAGIC_WEIGHTS) as ProfileMagicMilestoneId[]
  ).map((id) => ({
    id,
    label: MILESTONE_LABELS[id],
    weight: PROFILE_MAGIC_WEIGHTS[id],
    completed: completedById[id],
  }));

  const earned = milestones.reduce(
    (sum, m) => sum + (m.completed ? m.weight : 0),
    0,
  );
  const total = Object.values(PROFILE_MAGIC_WEIGHTS).reduce((a, b) => a + b, 0);

  return {
    percent: Math.min(100, Math.round(earned)),
    earned,
    total,
    milestones,
  };
}
